// Staleness/liveness heals extracted from main.tsx so the load-bearing
// invariants are unit-testable (REL-023/024/025). main.tsx wires the real
// onlineManager / supabase / queryClient into these; the logic lives here.
//
// Everything is dependency-injected (no imports of the app's singletons) so the
// helpers stay pure and free of module cycles.

export interface OnlineManagerLike {
  isOnline(): boolean
  setOnline(online: boolean): void
}

/**
 * REL-023/024 — onlineManager un-wedge.
 *
 * react-query pauses every 'online'-networkMode query while onlineManager
 * reports offline. Its flag only moves on window online/offline events, so a
 * MISSED 'online' event (frozen tab, roaming Wi-Fi) wedges the app: queries
 * paused, invalidate is a no-op, no error logged — the operator can only reload.
 *
 * THE INVARIANT (a regression that already shipped once, 2026-06-11): un-wedge
 * ONLY. NEVER force the flag FALSE from navigator.onLine — it false-reports
 * offline during wake-time Wi-Fi reassociation and on some VPN/virtual NICs, and
 * `setOnline(navigator.onLine)` promptly paused every query behind an eternal
 * spinner. So: set TRUE iff the OS says online but the manager is stuck offline;
 * otherwise do nothing. Returns true when it un-wedged (the caller then refetches).
 */
export function unwedgeOnlineManager(om: OnlineManagerLike, navigatorOnline: boolean): boolean {
  if (navigatorOnline && !om.isOnline()) {
    om.setOnline(true)
    return true
  }
  return false
}

/**
 * P2.8 (idle-wake audit 6.1) — probe-verified un-wedge for the CHRONIC-liar
 * state the plain un-wedge above can't reach: navigator.onLine stuck FALSE
 * (VPN/virtual NIC — documented fleet behaviour) after one 'offline' event.
 * Both signals read offline, so unwedgeOnlineManager never fires, every
 * query/poller stays paused, and nothing in src ever sets the flag true —
 * permanent and invisible until a reload.
 *
 * On each reconciler tick where BOTH signals say offline, count; at
 * `minTicks` consecutive ticks, fire ONE cache-busted probe (single-flight).
 * ANY HTTP response proves the link → setOnline(true) + onUnwedge (refetch +
 * queue drain). A probe failure just re-arms — the flag is NEVER forced
 * false (the 2026-06-11 invariant), and a genuinely-offline device probes at
 * most once per `minTicks` ticks. Do NOT reuse swRecovery.networkReachable()
 * as the probe — it short-circuits on navigator.onLine === false, i.e.
 * returns false without probing in exactly the state this exists for.
 */
export function createProbeUnwedge(opts: {
  om: OnlineManagerLike
  navigatorOnline: () => boolean
  /** Resolves true on ANY HTTP response; false only on a thrown network error. */
  probe: () => Promise<boolean>
  onUnwedge: () => void
  minTicks?: number
}): () => Promise<void> {
  const minTicks = opts.minTicks ?? 2
  let bothOfflineTicks = 0
  let probing = false
  return async () => {
    if (opts.navigatorOnline() || opts.om.isOnline()) { bothOfflineTicks = 0; return }
    bothOfflineTicks++
    if (bothOfflineTicks < minTicks || probing) return
    probing = true
    try {
      if (await opts.probe()) {
        opts.om.setOnline(true)
        bothOfflineTicks = 0
        opts.onUnwedge()
      } else {
        // Re-arm: next both-offline tick may probe again after minTicks more.
        bothOfflineTicks = 0
      }
    } finally {
      probing = false
    }
  }
}

/**
 * REL-025 — auth-heal invalidate. When the JWT actually refreshes
 * (TOKEN_REFRESHED), refetch active queries so anything that errored or returned
 * an RLS-empty "success" during a dead-token window repopulates without a reload.
 * Only TOKEN_REFRESHED triggers it (SIGNED_IN/SIGNED_OUT/etc. must not).
 *
 * `subscribe` is supabase.auth.onAuthStateChange adapted to a plain
 * event-string callback; `invalidate` is queryClient.invalidateQueries.
 */
export function installTokenRefreshHeal(
  subscribe: (cb: (event: string) => void) => void,
  invalidate: () => void,
): void {
  subscribe((event) => {
    if (event === 'TOKEN_REFRESHED') invalidate()
  })
}

export const WAKE_HEAL_AFTER_MS = 5 * 60_000
export const SLEEP_FREEZE_MS = 90_000

/**
 * REL-019/020/021 — wake heal. With refetchOnWindowFocus off, a tab that was
 * hidden/asleep for a while comes back stale (pollers skipped, sockets died).
 * Returns a visibilitychange handler: record the time on 'hidden', and on
 * 'visible' refetch ONCE — but only after a real absence (> thresholdMs), so
 * seconds-long scanner/camera alt-tabs cost nothing (the storm that got
 * focus-refetch turned off stays dead). `now` is injected for testability.
 */
export function createWakeHeal(opts: {
  now: () => number
  invalidate: () => void
  thresholdMs?: number
}): (visibilityState: DocumentVisibilityState) => void {
  const threshold = opts.thresholdMs ?? WAKE_HEAL_AFTER_MS
  let hiddenAt: number | null = null
  return (visibilityState) => {
    if (visibilityState === 'hidden') { hiddenAt = opts.now(); return }
    const awayMs = hiddenAt === null ? 0 : opts.now() - hiddenAt
    hiddenAt = null
    if (awayMs > threshold) opts.invalidate()
  }
}

/**
 * REL-019/020/021 — sleep detector. visibilitychange never fires when a desktop
 * display sleeps with the app frontmost, so the wake heal misses it. A frozen
 * heartbeat is the signal every sleep variant shares: returns a tick() to call
 * on a `tickMs` interval; it measures its own delay and calls onFreeze when the
 * gap since the last tick exceeds tickMs by ≥ freezeMs (the machine was
 * suspended). `now` is injected for testability.
 */
export function createSleepDetector(opts: {
  now: () => number
  tickMs: number
  onFreeze: () => void
  freezeMs?: number
}): () => void {
  const freeze = opts.freezeMs ?? SLEEP_FREEZE_MS
  let lastTickAt = opts.now()
  return () => {
    const now = opts.now()
    const frozenMs = now - lastTickAt - opts.tickMs
    lastTickAt = now
    if (frozenMs >= freeze) opts.onFreeze()
  }
}
